// saarthi-webapp/src/components/private/highgarden/favorites/UserFavoriteProducts.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ListGroup, Badge, Container, Col, Row, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash';
import ConfirmationModal from '../../common_components/ConfirmationModal';
import ProductModal from '../baseData/ProductModal';
import ProductBrandModal from '../baseData/ProductBrandModal';
import { getItemMeasure } from '../services';

const UserFavoriteProducts = () => {
    const [products, setProducts] = useState([]);
    const [nextUrl, setNextUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem('token');
    const listGroupRef = useRef(null);

    const [defaultGroceryList, setDefaultGroceryList] = useState(null);

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [productToRemove, setProductToRemove] = useState(null);

    // Search state
    const [searchValue, setSearchValue] = useState(null);
    const [initialProductName, setInitialProductName] = useState('');

    // Modal states
    const [showProductModal, setShowProductModal] = useState(false);
    const [showBrandModal, setShowBrandModal] = useState(false);

    const pageSize = 10;

    // Memoized fetch function for favorite products
    const fetchUserFavoriteProducts = useCallback(async (nextUrl = null) => {
        setLoading(true); // Set loading to true when fetching starts
        const params = {
            page_size: pageSize
        };

        const url = nextUrl || `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-products/`;
        try {
            const response = await axios.get(url, {
                params,
                headers: {
                    'Authorization': `Token ${token}`
                }
            });

            if (nextUrl) {
                setProducts(prevProducts => [...prevProducts, ...response.data.results]);
            } else {
                setProducts(response.data.results);
            }
            setNextUrl(response.data.next);
        } catch (error) {
            console.error("Error fetching user favorite products:", error);
            toast.error("Error fetching user favorite products!");
        } finally {
            setLoading(false); // Set loading to false when fetching completes
        }
    }, [token, pageSize]);

    useEffect(() => {
        fetchUserGroceryLists();
    }, []); // Empty dependency array ensures this runs only once on mount

    useEffect(() => {
        setProducts([]); // Reset products list on page load
        setNextUrl(null); // Reset pagination URL on page load
        fetchUserFavoriteProducts();
    }, [fetchUserFavoriteProducts]);

    useEffect(() => {
        const handleScroll = () => {
            if (listGroupRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = listGroupRef.current;

                if (scrollTop + clientHeight >= scrollHeight - 20 && nextUrl && !loading) {
                    fetchUserFavoriteProducts(nextUrl);
                }
            }
        };

        const listGroupElement = listGroupRef.current;
        if (listGroupElement) {
            listGroupElement.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (listGroupElement) {
                listGroupElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [nextUrl, loading, fetchUserFavoriteProducts]);

    const fetchUserGroceryLists = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/household-grocery-lists/`, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            if (response.data.results.length > 0) {
                const defaultList = response.data.results.find(list => list.is_default === true);
                setDefaultGroceryList(defaultList || response.data.results[0]);
            } else {
                setDefaultGroceryList(null);
                console.warn("No grocery lists found.");
            }
        } catch (error) {
            console.error("Error fetching user grocery lists:", error);
        }
    };

    const addProductToGroceryList = async (product) => {
        if (!defaultGroceryList) {
            console.error("No default grocery list found.");
            return;
        }
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/`, {
                list_uuid: defaultGroceryList.uuid,
                name: product.product_details.name,
                quantity: 1,
                product: product.product_details.id
            }, {
                headers: { 'Authorization': `Token ${token}` }
            });
            toast.success(`Product added to ${defaultGroceryList.name}!`);
        } catch (error) {
            toast.error("Error adding product to grocery list!");
            console.error("Error adding product to grocery list:", error);
        }
    };

    // use shared helper
    // const getProductMeasure = (product) => getItemMeasure(product);

    const handleRemoveFavorite = async (productId) => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-products/${productId}/`, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            setProductToRemove(null);
            setShowConfirmationModal(false);
            setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
            toast.success("Product removed from favorites successfully!");
        } catch (error) {
            toast.error("Error removing favorite products!");
            console.error("Error removing favorite products:", error);
        }
    };

    // Fetch products for search dropdown - memoized to prevent dependency chain issues
    const fetchProductsForSearch = useCallback(async (inputValue) => {
        if (inputValue.length < 3) return [];
        
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-products/`, {
                params: { search: inputValue, page_size: 20 },
                headers: { 'Authorization': `Token ${token}` }
            });

            const productOptions = response.data.results.map(product => ({
                value: product.id,
                label: `${product.product_brand.name} - ${product.name} ${getItemMeasure(product)}`,
                isProduct: true,
                productData: product
            }));

            // If no results found, add "Add Product" option
            if (productOptions.length === 0) {
                return [{
                    value: 'add-new-product',
                    label: `➕ Add "${inputValue}" as new product`,
                    isAddNew: true,
                    searchTerm: inputValue
                }];
            }

            return productOptions;
        } catch (error) {
            console.error('Error fetching products:', error);
            return [];
        }
    }, [token]);

    const debouncedFetchProducts = useCallback(
        (inputValue, callback) => {
            const debouncedFn = debounce((value) => {
                if (value.length >= 3) {
                    fetchProductsForSearch(value).then(callback);
                } else {
                    callback([]);
                }
            }, 500);
            debouncedFn(inputValue);
        },
        [fetchProductsForSearch]
    );

    // Handle selection from dropdown
    const handleSelectChange = async (selectedOption) => {
        if (!selectedOption) {
            setSearchValue(null);
            return;
        }

        if (selectedOption.isAddNew) {
            // Open modal to add new product with pre-filled name
            setInitialProductName(selectedOption.searchTerm);
            setShowProductModal(true);
            setSearchValue(null);
        } else if (selectedOption.isProduct) {
            // Add to favorites
            await addToFavorites(selectedOption.value);
            setSearchValue(null);
        }
    };

    // Add product to favorites
    const addToFavorites = async (productId) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-products/`, {
                product_id: productId
            }, {
                headers: { 'Authorization': `Token ${token}` }
            });
            toast.success("Product added to favorites!");
            fetchUserFavoriteProducts(); // Refresh favorites list
        } catch (error) {
            if (error.response && error.response.status === 400) {
                toast.info("Product is already in your favorites!");
            } else {
                toast.error("Error adding product to favorites!");
            }
            console.error("Error adding favorite product:", error);
        }
    };

    // Custom option component to show plus icon
    const formatOptionLabel = ({ label, isAddNew, isProduct }) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{label}</span>
            {isProduct && (
                <i className="bi bi-plus-circle text-success" style={{ fontSize: '1.2em' }}></i>
            )}
        </div>
    );

    // Handle product modal close and refresh
    const handleProductModalClose = () => {
        setShowProductModal(false);
        setInitialProductName('');
        // Refresh favorites list after adding product
        fetchUserFavoriteProducts();
    };

    // Handle brand modal close
    const handleBrandModalClose = () => {
        setShowBrandModal(false);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    {/* Simple Search Interface */}
                    <div className="mb-4">
                        <AsyncSelect
                            value={searchValue}
                            loadOptions={debouncedFetchProducts}
                            onChange={handleSelectChange}
                            placeholder="Search products to add to favorites..."
                            isClearable
                            formatOptionLabel={formatOptionLabel}
                            noOptionsMessage={({ inputValue }) => 
                                inputValue.length < 3 
                                    ? "Type at least 3 characters to search" 
                                    : "No products found"
                            }
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: '45px',
                                    fontSize: '16px'
                                }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 9999
                                })
                            }}
                        />
                    </div>

                    {/* Favorites List */}
                    <h5 className="mb-3">My Favorite Products</h5>
                    <ListGroup ref={listGroupRef} className="hidden-scrollbar" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {products.map((product) => (
                            <ListGroup.Item key={product.id} className="d-flex justify-content-between align-items-center">
                                <div>
                                    <Badge bg="none" style={{ backgroundColor: '#D5120B', color: 'white', marginRight: '7px', minWidth: '90px', textAlign: 'left' }}>
                                        {product.product_details.product_brand.website ?
                                            <a href={product.product_details.product_brand.website} target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>
                                                {product.product_details.product_brand.name}
                                            </a> :
                                            product.product_details.product_brand.name
                                        }
                                    </Badge>
                                    {product.product_details.name} - {getItemMeasure(product)}
                                </div>
                                <div>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${product.id}`}>Add to default grocery list</Tooltip>}
                                    >
                                        <i className="bi bi-plus-circle"
                                            style={{ cursor: 'pointer', marginRight: '7px' }}
                                            onClick={(e) => {
                                                addProductToGroceryList(product);
                                                e.stopPropagation();
                                            }}
                                        ></i>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${product.id}`}>Remove from favorites</Tooltip>}
                                    >
                                        <i className="bi bi-trash"
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                setProductToRemove(product.id);
                                                setShowConfirmationModal(true);
                                                e.stopPropagation();
                                            }}
                                        >
                                        </i>
                                    </OverlayTrigger>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    {loading && (
                        <div className="d-flex justify-content-center mt-2">
                            <Spinner animation="border" />
                        </div>
                    )}
                </Col>
            </Row>

            {/* Modals */}
            <ConfirmationModal
                show={showConfirmationModal}
                onHide={() => setShowConfirmationModal(false)}
                onConfirm={() => handleRemoveFavorite(productToRemove)}
                title="Remove from favorites"
                body="Are you sure you want to remove this product from your favorites?"
            />

            <ProductModal
                show={showProductModal}
                handleClose={handleProductModalClose}
                refreshProducts={() => {}}
                initialProductName={initialProductName}
            />

            <ProductBrandModal
                show={showBrandModal}
                handleClose={handleBrandModalClose}
            />
        </Container>
    );
};

export default UserFavoriteProducts;
